import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { api, apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cardImageStyle, CardHoverPreview } from "@/components/Card3D";
import { Send, ShieldAlert, ImagePlus, X, Search, ChevronLeft, ChevronRight, Repeat, Flag, Users, Swords } from "lucide-react";
import { toast } from "sonner";

const PICK_RARITIES = ["all", "common", "uncommon", "rare", "epic", "legendary", "goddess"];
const PICK_PAGE = 10;

function timeAgo(iso) {
  try {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return "now";
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    return `${Math.floor(s / 86400)}d`;
  } catch { return ""; }
}

function CardChip({ card, ownerId, ownerName, currentUserId }) {
  const navigate = useNavigate();
  const landscape = card.orientation === "landscape";
  const canTrade = !!(ownerId && currentUserId && ownerId !== currentUserId);
  const tradeAction = canTrade ? (
    <Button
      size="sm"
      data-testid={`chat-offer-trade-${card.id}`}
      onClick={() => navigate(`/requests?want_card=${encodeURIComponent(card.id)}&want_title=${encodeURIComponent(card.title || "")}`)}
      className="w-full bg-cyan-400 text-black hover:bg-cyan-300 h-7 text-xs font-medium"
    >
      <Repeat className="h-3.5 w-3.5 mr-1.5" /> Post a Trade Request
    </Button>
  ) : null;
  return (
    <CardHoverPreview card={card} side="right" action={tradeAction}>
      <Link to={`/card/${card.id}`} data-testid={`chat-card-${card.id}`}
        className={`mt-1 inline-flex items-center gap-2 rounded-lg border border-white/10 bg-black/40 p-1.5 pr-3 hover:border-cyan-400/50 transition-colors rarity-${card.rarity}`}>
        <div className={`relative ${landscape ? "w-14 aspect-[3.5/2.5]" : "w-9 aspect-[2.5/3.5]"} rounded overflow-hidden bg-black shrink-0`}>
          {card.front_image && <img src={card.front_image} alt={card.title} className="absolute inset-0 w-full h-full" style={cardImageStyle(card)} draggable={false} />}
          <div className="foil-overlay" />
        </div>
        <div className="min-w-0">
          <div className="text-white text-xs font-medium truncate max-w-[140px]">{card.title || "Card"}</div>
          <div className="font-mono text-[9px] uppercase tracking-[0.15em]" style={{ color: "var(--label)" }}>
            {card.rarity}{card.edition_number ? ` · #${card.edition_number}` : ""}
          </div>
        </div>
      </Link>
    </CardHoverPreview>
  );
}

function SquadReveal({ cards }) {
  return (
    <div className="mt-1 space-y-1" data-testid="chat-squad-reveal">
      {cards.map((c, i) => {
        const landscape = c.orientation === "landscape";
        return (
          <CardHoverPreview key={c.id || i} card={c} side="right">
            <div data-testid={`chat-reveal-card-${i}`}
              className={`inline-flex items-center gap-2 rounded-lg border border-rose-400/25 bg-black/40 p-1.5 pr-3 hover:border-rose-400/60 transition-colors rarity-${c.rarity} w-full`}>
              <div className={`relative ${landscape ? "w-14 aspect-[3.5/2.5]" : "w-9 aspect-[2.5/3.5]"} rounded overflow-hidden bg-black shrink-0`}>
                {c.front_image && <img src={c.front_image} alt={c.title} className="absolute inset-0 w-full h-full" style={cardImageStyle(c)} draggable={false} />}
                <div className="foil-overlay" />
              </div>
              <div className="min-w-0">
                <div className="text-white text-xs font-medium truncate max-w-[150px]">{c.title || "Soldier"}</div>
                <div className="font-mono text-[9px] uppercase tracking-[0.15em]" style={{ color: "var(--label)" }}>{c.rarity}</div>
              </div>
            </div>
          </CardHoverPreview>
        );
      })}
    </div>
  );
}

function CardPicker({ onPick, onClose }) {
  const { data: cards } = useQuery({
    queryKey: ["my-cards"],
    queryFn: async () => (await api.get("/cards/mine")).data,
  });
  const [search, setSearch] = useState("");
  const [rarity, setRarity] = useState("all");
  const [page, setPage] = useState(0);

  const filtered = useMemo(() => {
    let l = cards || [];
    if (search.trim()) l = l.filter((c) => (c.title || "").toLowerCase().includes(search.toLowerCase()));
    if (rarity !== "all") l = l.filter((c) => c.rarity === rarity);
    return l;
  }, [cards, search, rarity]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PICK_PAGE));
  const safePage = Math.min(page, pageCount - 1);
  const shown = filtered.slice(safePage * PICK_PAGE, safePage * PICK_PAGE + PICK_PAGE);

  return (
    <div className="mb-2 rounded-lg border border-white/10 bg-[hsl(220_18%_9%)] p-2" data-testid="chat-card-picker">
      <div className="flex items-center justify-between mb-1.5">
        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/45">Share a card</span>
        <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="chat-card-picker-close"><X className="h-3.5 w-3.5" /></button>
      </div>
      <div className="flex gap-1.5 mb-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-white/30" />
          <Input data-testid="chat-pick-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} placeholder="Search…" className="h-7 pl-6 bg-[hsl(220_18%_12%)] border-white/10 text-white text-xs" />
        </div>
        <Select value={rarity} onValueChange={(v) => { setRarity(v); setPage(0); }}>
          <SelectTrigger data-testid="chat-pick-rarity" className="h-7 w-24 bg-[hsl(220_18%_12%)] border-white/10 text-white text-[11px] capitalize"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[hsl(222_22%_8%)] border-white/10 text-white">{PICK_RARITIES.map((r) => <SelectItem key={r} value={r} className="text-xs capitalize">{r}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {filtered.length === 0 ? (
        <div className="text-white/40 text-xs py-3 text-center">No cards match.</div>
      ) : (
        <>
          <div className="grid grid-cols-5 gap-1.5">
            {shown.map((c) => (
              <CardHoverPreview key={c.id} card={c} side="top">
                <button type="button" data-testid={`chat-pick-card-${c.id}`} onClick={() => onPick(c)}
                  title={c.title}
                  className={`relative aspect-[2.5/3.5] rounded overflow-hidden bg-black border border-white/10 hover:border-cyan-400/60 transition-colors rarity-${c.rarity}`}>
                  {c.front_image && <img src={c.front_image} alt={c.title} className="absolute inset-0 w-full h-full" style={cardImageStyle(c)} draggable={false} />}
                  <div className="foil-overlay" />
                </button>
              </CardHoverPreview>
            ))}
          </div>
          {filtered.length > PICK_PAGE && (
            <div className="flex items-center justify-between mt-2">
              <span className="font-mono text-[9px] text-white/35">{filtered.length} · {safePage + 1}/{pageCount}</span>
              <div className="flex gap-1">
                <button data-testid="chat-pick-prev" disabled={safePage === 0} onClick={() => setPage(safePage - 1)} className="h-6 w-6 rounded border border-white/15 text-white/60 disabled:opacity-30 flex items-center justify-center"><ChevronLeft className="h-3.5 w-3.5" /></button>
                <button data-testid="chat-pick-next" disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)} className="h-6 w-6 rounded border border-white/15 text-white/60 disabled:opacity-30 flex items-center justify-center"><ChevronRight className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function BroadcastChip({ b, isOpen, navigate }) {
  const Icon = b.kind === "race" ? Flag : b.kind === "cavern" ? Users : Swords;
  return (
    <button
      data-testid={`chat-broadcast-${b.ref_id}`}
      disabled={!isOpen}
      onClick={() => isOpen && navigate(b.to)}
      className={`mt-1 w-full flex items-center gap-2 rounded-lg border p-2 text-left transition-colors ${isOpen ? "border-cyan-400/40 bg-cyan-400/[0.07] hover:bg-cyan-400/15" : "border-white/10 bg-white/[0.02] opacity-45 cursor-not-allowed"}`}
    >
      <span className={`h-7 w-7 rounded-md flex items-center justify-center shrink-0 ${isOpen ? "bg-cyan-400/20" : "bg-white/5"}`}>
        <Icon className={`h-3.5 w-3.5 ${isOpen ? "text-cyan-300" : "text-white/40"}`} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-white text-xs font-medium capitalize">{b.kind} · {isOpen ? "Open — join now" : "Closed"}</span>
        <span className="block font-mono text-[10px] text-white/45 truncate">{b.label}</span>
      </span>
      {isOpen && <span className="text-[10px] font-mono text-cyan-300 shrink-0">JOIN →</span>}
    </button>
  );
}

export function ChatPanel({ channel, heightClass = "h-72", heightPx = null, fillHeight = false, testidPrefix = "chat", lfgKind = null }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [mutedLeft, setMutedLeft] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [attached, setAttached] = useState(null);
  const scrollRef = useRef(null);

  // For LFG channels, know which broadcast targets are still open (to grey out the rest).
  const { data: lfgData } = useQuery({
    queryKey: ["lfg", lfgKind],
    queryFn: async () => (await api.get(lfgKind === "race" ? "/race/open" : lfgKind === "cavern" ? "/cavern/open" : "/arena/battles/open")).data,
    refetchInterval: 5000,
    enabled: !!lfgKind && !!user,
  });
  const openIds = useMemo(() => {
    if (!lfgData) return null;
    if (lfgKind === "arena") return new Set([...(lfgData.open || []), ...(lfgData.mine || [])].map((b) => b.id));
    return new Set((lfgData || []).map((r) => r.id));
  }, [lfgData, lfgKind]);

  const { data } = useQuery({
    queryKey: ["chat", channel],
    queryFn: async () => (await api.get(`/chat/${encodeURIComponent(channel)}`)).data,
    // SSE (below) delivers new messages instantly; this is just a slow safety-net
    // resync in case a connection drop is missed by the EventSource auto-reconnect.
    refetchInterval: 20000,
    enabled: !!channel,
  });
  const messages = data?.messages || [];

  // Live stream — new messages are pushed the instant they're posted, no polling.
  useEffect(() => {
    if (!channel) return;
    const url = `${API}/chat/${encodeURIComponent(channel)}/stream`;
    const es = new EventSource(url, { withCredentials: true });
    es.onmessage = (e) => {
      if (!e.data) return;
      let m;
      try { m = JSON.parse(e.data); } catch { return; }
      qc.setQueryData(["chat", channel], (old) => {
        const cutoff = Math.max(Date.now() - 7 * 86400000, Date.parse(old?.cleared_before || "") || 0);
        if (!m.created_at || Date.parse(m.created_at) <= cutoff) return old;
        const prevMessages = old?.messages || [];
        if (prevMessages.some((x) => x.id === m.id)) return old;
        return { ...(old || {}), messages: [...prevMessages.filter(x => Date.parse(x.created_at) > cutoff), m].slice(-200) };
      });
    };
    return () => es.close();
  }, [channel, qc]);

  useEffect(() => {
    if (data?.banned_seconds_left != null) setMutedLeft(data.banned_seconds_left);
  }, [data?.banned_seconds_left]);

  useEffect(() => {
    if (mutedLeft <= 0) return;
    const t = setInterval(() => setMutedLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [mutedLeft]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const send = useMutation({
    mutationFn: async (payload) => (await api.post(`/chat/${encodeURIComponent(channel)}`, payload)).data,
    // No need to invalidate/refetch: the SSE stream above pushes this exact
    // message back to us the instant it's published, so the UI updates
    // in real time with zero extra network round-trip.
    onSuccess: () => { setText(""); setAttached(null); setPickerOpen(false); },
    onError: (e) => {
      const msg = apiError(e, "Couldn't send");
      toast.error(msg);
      const m = /muted for (\d+)\s*minute/i.exec(msg) || /muted for (\d+)s/i.exec(msg);
      if (m) {
        const val = msg.toLowerCase().includes("minute") ? Number(m[1]) * 60 : Number(m[1]);
        setMutedLeft(val);
      }
    },
  });

  const submit = (e) => {
    e?.preventDefault();
    const t = text.trim();
    if ((!t && !attached) || send.isPending || mutedLeft > 0) return;
    send.mutate({ text: t, card_id: attached?.id || null });
  };

  return (
    <div className={`flex flex-col min-w-0 ${fillHeight ? "flex-1 min-h-0" : ""}`} data-testid={`${testidPrefix}-panel`}>
      <div ref={scrollRef} className={`${fillHeight ? "flex-1 min-h-0" : heightPx ? "" : heightClass} overflow-y-auto overflow-x-hidden overscroll-contain [overflow-wrap:anywhere] space-y-2 pr-1`} style={!fillHeight && heightPx ? { height: heightPx } : undefined} data-testid={`${testidPrefix}-messages`}>
        {messages.length === 0 ? (
          <div className="text-white/35 text-xs font-mono text-center py-6">No messages yet — say hello.</div>
        ) : (
          messages.map((m) => {
            const mine = user && m.user_id === user.id;
            return (
              <div key={m.id} className="text-sm" data-testid={`${testidPrefix}-message-${m.id}`}>
                <span className={`font-mono text-[11px] ${m.role === "ai" ? "text-rose-300" : m.role === "admin" ? "text-amber-300" : mine ? "text-cyan-300" : "text-emerald-300/90"}`}>
                  {m.user_name}{m.role === "admin" ? " ★" : ""}{m.role === "ai" ? " ⚙" : ""}
                </span>
                <span className="text-white/25 font-mono text-[9px] ml-1.5">{timeAgo(m.created_at)}</span>
                {m.text && <div className="text-white/80 leading-snug break-words">{m.text}</div>}
                {m.card && <CardChip card={m.card} ownerId={m.user_id} ownerName={m.user_name} currentUserId={user?.id} />}
                {m.cards && m.cards.length > 0 && <SquadReveal cards={m.cards} />}
                {m.broadcast && <BroadcastChip b={m.broadcast} isOpen={openIds ? openIds.has(m.broadcast.ref_id) : true} navigate={navigate} />}
              </div>
            );
          })
        )}
      </div>

      {!user ? (
        <div className="mt-3 text-xs text-white/40 font-mono text-center">Sign in to join the chat.</div>
      ) : mutedLeft > 0 ? (
        <div className="mt-3 flex items-center gap-2 rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-xs text-red-300" data-testid={`${testidPrefix}-muted`}>
          <ShieldAlert className="h-4 w-4 shrink-0" /> You're muted for {mutedLeft}s (hate-speech violation).
        </div>
      ) : (
        <div className={`mt-3 ${fillHeight ? "shrink-0 max-h-[65%] overflow-y-auto overscroll-contain" : ""}`}>
          {pickerOpen && <CardPicker onPick={(c) => { setAttached(c); setPickerOpen(false); }} onClose={() => setPickerOpen(false)} />}
          {attached && (
            <div className="mb-2 flex items-center gap-2 rounded-lg border border-cyan-400/30 bg-cyan-400/5 px-2 py-1.5" data-testid={`${testidPrefix}-attached`}>
              <div className={`relative w-7 aspect-[2.5/3.5] rounded overflow-hidden bg-black rarity-${attached.rarity} shrink-0`}>
                {attached.front_image && <img src={attached.front_image} alt={attached.title} className="absolute inset-0 w-full h-full" style={cardImageStyle(attached)} draggable={false} />}
              </div>
              <span className="text-white/80 text-xs truncate flex-1">Attached: {attached.title}</span>
              <button onClick={() => setAttached(null)} className="text-white/40 hover:text-white" data-testid={`${testidPrefix}-remove-attached`}><X className="h-3.5 w-3.5" /></button>
            </div>
          )}
          <form onSubmit={submit} className="flex items-center gap-2">
            <Button type="button" size="icon" variant="outline" data-testid={`${testidPrefix}-attach-card`}
              onClick={() => setPickerOpen((o) => !o)}
              className="border-white/15 text-cyan-300 hover:bg-white/5 shrink-0">
              <ImagePlus className="h-4 w-4" />
            </Button>
            <Input
              data-testid={`${testidPrefix}-input`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={500}
              placeholder={attached ? "Add a note (optional)…" : "Message…"}
              className="min-w-0 bg-[hsl(220_18%_12%)] border-white/10 text-white"
            />
            <Button type="submit" size="icon" data-testid={`${testidPrefix}-send`} disabled={send.isPending || (!text.trim() && !attached)} className="bg-cyan-400 text-black hover:bg-cyan-300 shrink-0">
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      )}
    </div>
  );
}
